/**
 * Scan chapters for references to database entity names. Markdown is scanned
 * as-is; `.zwdoc` through its plain-text projection (`scanTextOf`).
 */

import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  matchMaybe,
  type Maybe,
} from "./maybe";
import { possessiveSuffixLength } from "./nameForms";
import { firstTitle, paragraphLocation, scanTextOf } from "./zwdoc/scanText";

const ATX_H1_RE = /^#\s+(.*)$/;
const FENCE_RE = /^(`{3,}|~{3,})(.*)$/;

export type EntityOccurrence = {
  /** Chapter basename (e.g. `Chapter 1.md`). */
  file: string;
  /** Offset of the match start: Markdown text, or `.zwdoc` projection. */
  from: number;
  /** Offset of the match end (exclusive). */
  to: number;
  /** What `lineNumber` counts: Markdown lines or `.zwdoc` paragraphs. */
  unit: "line" | "paragraph";
  lineNumber: number;
  /** Trimmed line / paragraph text for display. */
  snippet: string;
};

/** The subset of a database entry that determines what counts as an
 * occurrence of it: its display name plus any `aka` aliases (#72). */
export type DatabaseEntryNames = {
  name: string;
  aka: string[];
  /** Optional plural as written in prose (e.g. `Vulcans`). */
  plural: Maybe<string>;
  /** Optional adjective or demonym (e.g. `English` for England). */
  adjective: Maybe<string>;
};

function fileStem(name: string): string {
  const stem = name.replace(/\.[^.]+$/, "");
  return stem.length > 0 ? stem : name;
}

function stripInlineMarkdown(raw: string): string {
  return raw
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_~`]+/g, "")
    .replace(/\s+#+\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function fenceMarker(line: string): Maybe<string> {
  const fenceMatch = toMaybe(line.match(FENCE_RE));
  if (isNothing(fenceMatch)) return nothing;
  return just((fenceMatch.value[1] ?? "").charAt(0));
}

function nextFence(fence: Maybe<string>, marker: string): Maybe<string> {
  if (isNothing(fence)) return just(marker);
  return marker === fence.value ? nothing : fence;
}

type TitleScan = {
  readonly fence: Maybe<string>;
  readonly title: Maybe<string>;
};

function scanTitleLine(state: TitleScan, line: string): TitleScan {
  if (isJust(state.title)) return state;
  const marker = fenceMarker(line);
  if (isJust(marker)) {
    return { fence: nextFence(state.fence, marker.value), title: nothing };
  }
  if (isJust(state.fence)) return state;
  const match = toMaybe(line.match(ATX_H1_RE));
  if (isNothing(match)) return state;
  const text = stripInlineMarkdown(match.value[1] ?? "");
  return text.length > 0 ? { fence: state.fence, title: just(text) } : state;
}

/** First H1 text (ATX in Markdown, level-1 heading in `.zwdoc`), else the file stem. */
export function entityDisplayName(markdown: string, fileName: string): string {
  const scan = scanTextOf(markdown);
  if (scan.kind === "zwdoc") {
    return matchMaybe(firstTitle(scan.projection), {
      just: (title) => title,
      nothing: () => fileStem(fileName),
    });
  }
  const found = markdown.split("\n").reduce(scanTitleLine, {
    fence: nothing,
    title: nothing,
  });
  return matchMaybe(found.title, {
    just: (title) => title,
    nothing: () => fileStem(fileName),
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** The distinct, non-empty strings that count as a match for a database
 * entry: display name, optional plural/adjective forms, and `aka` aliases.
 * Shared by occurrence scan, hover peek, and Harper dictionary. */
export function findOccurrenceNames(entry: DatabaseEntryNames): string[] {
  return Array.from(
    new Set(
      [
        entry.name,
        ...[entry.plural, entry.adjective].flatMap((value) =>
          isJust(value) ? [value.value] : [],
        ),
        ...entry.aka,
      ]
        .map((name) => name.trim())
        .filter((name) => name.length > 0),
    ),
  );
}

/** Case-sensitive whole-word matches for a single name, skipping fenced code
 * blocks unless `skipFences` is false (plain text has no fences). Returns
 * offsets within `markdown` only (caller attaches `file`). */
function findNameOccurrencesInDoc(
  markdown: string,
  name: string,
  skipFences: boolean,
): Omit<EntityOccurrence, "file">[] {
  const needle = name.trim();
  if (needle.length === 0) return [];

  const re = new RegExp(
    `(?<![\\p{L}\\p{N}_])${escapeRegExp(needle)}(?![\\p{L}\\p{N}_])`,
    "gu",
  );
  const scanned = markdown
    .split("\n")
    .reduce<LineScan>(
      (state, line) => scanHitLine(state, line, markdown, re, skipFences),
      { offset: 0, lineNumber: 1, fence: nothing, hits: [] },
    );
  return [...scanned.hits];
}

type LineHit = Omit<EntityOccurrence, "file">;

type LineScan = {
  readonly offset: number;
  readonly lineNumber: number;
  readonly fence: Maybe<string>;
  readonly hits: readonly LineHit[];
};

function lineHits(
  line: string,
  offset: number,
  lineNumber: number,
  markdown: string,
  re: RegExp,
): LineHit[] {
  re.lastIndex = 0;
  const hits: LineHit[] = [];
  for (const match of line.matchAll(re)) {
    const text = match[0] ?? "";
    const index = match.index ?? 0;
    if (text.length === 0) continue;
    const from = offset + index;
    const baseLen = text.length;
    const extra = possessiveSuffixLength(markdown, from + baseLen);
    hits.push({
      from,
      to: from + baseLen + extra,
      unit: "line",
      lineNumber,
      snippet: line.trim(),
    });
  }
  return hits;
}

function scanHitLine(
  state: LineScan,
  line: string,
  markdown: string,
  re: RegExp,
  skipFences: boolean,
): LineScan {
  const marker = skipFences ? fenceMarker(line) : nothing;
  if (isJust(marker)) {
    return {
      fence: nextFence(state.fence, marker.value),
      offset: state.offset + line.length + 1,
      lineNumber: state.lineNumber + 1,
      hits: state.hits,
    };
  }
  const hits = isNothing(state.fence)
    ? lineHits(line, state.offset, state.lineNumber, markdown, re)
    : [];
  return {
    fence: state.fence,
    offset: state.offset + line.length + 1,
    lineNumber: state.lineNumber + 1,
    hits: hits.length > 0 ? [...state.hits, ...hits] : state.hits,
  };
}

/**
 * Case-sensitive whole-word matches against an entry's name and `aka`
 * aliases, skipping fenced code blocks unless `skipFences` is false. Returns
 * offsets within `markdown` only (caller attaches `file`).
 */
export function findOccurrencesInDoc(
  markdown: string,
  entry: DatabaseEntryNames,
  skipFences = true,
): Omit<EntityOccurrence, "file">[] {
  const needles = findOccurrenceNames(entry).sort((a, b) => {
    const byLength = b.length - a.length;
    return byLength === 0 ? a.localeCompare(b) : byLength;
  });
  const hits = needles.flatMap((name) =>
    findNameOccurrencesInDoc(markdown, name, skipFences),
  );
  const seen = new Set<number>();
  return hits
    .filter((hit) => (seen.has(hit.from) ? false : (seen.add(hit.from), true)))
    .sort((a, b) => a.from - b.from);
}

/** Occurrences in one chapter file's raw text (Markdown or `.zwdoc`). */
export function findOccurrencesInFile(
  file: string,
  raw: string,
  entry: DatabaseEntryNames,
): EntityOccurrence[] {
  const scan = scanTextOf(raw);
  if (scan.kind === "markdown") {
    return findOccurrencesInDoc(scan.text, entry).map((hit) => ({
      ...hit,
      file,
    }));
  }
  const { projection } = scan;
  return findOccurrencesInDoc(projection.text, entry, false).map((hit) => {
    const place = paragraphLocation(projection, hit.from);
    return {
      file,
      from: hit.from,
      to: hit.to,
      unit: place.unit,
      lineNumber: place.number,
      snippet: place.snippet,
    };
  });
}
